// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  Input,
  OnChanges,
  signal,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { StatusColorService } from '../services/status-color.service';

import { APICallGateComponent } from '../../shared/components/api-call-gate.component';
import { ServicePartsComponent } from '../service-parts/service-parts.component';
import { ServiceTicketsComponent } from '../service-tickets/service-tickets.component';
import {
  SparePartsTab,
  SparePartsTabsComponent,
} from '../spare-parts-tabs/spare-parts-tabs.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SparePartsOverviewService } from '../services/spare-parts-overview.service';
import { SpOperationalState } from '../enum/spare-parts-enums';
import { SpPartsModel } from '../interfaces/sp-parts-model';
import { SpSortEventModel } from '../model/sp-sort-event-model';
import { SpTicketModel } from '../interfaces/sp-ticket-model';
import moment from 'moment';
import { ISO_DATE_FORMAT } from '../spare-parts-datepicker/spare-parts-datepicker.component';
import { MostConsumedWidgetComponent } from '../most-consumed-widget/most-consumed-widget.component';
import { ApplicationInsightsService } from '../../portal/services/application-insights/application-insights-service/application-insights.service';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SparePartsTabsComponent,
    ServiceTicketsComponent,
    ServicePartsComponent,
    APICallGateComponent,
    MostConsumedWidgetComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-spare-parts-panel',
  templateUrl: './spare-parts-panel.component.html',
  styleUrl: './spare-parts-panel.component.scss',
})
export class SparePartsPanelComponent implements OnChanges, OnInit {
  @Input({ required: true }) systemMaterialNumber!: number;
  @Input({ required: true }) systemSerialNumber!: string;

  protected readonly TAB_SERVICE_TICKETS: SparePartsTab = 'serviceTickets';
  protected readonly TAB_SERVICE_PARTS: SparePartsTab = 'serviceParts';
  protected readonly selectedTab = signal<SparePartsTab>(
    this.TAB_SERVICE_TICKETS,
  );
  protected redirectButtonLabel = computed(() => {
    let label = 'spareParts.allServiceTickets';
    if (this.TAB_SERVICE_PARTS === this.selectedTab()) {
      label = 'spareParts.allServiceParts';
    }

    return label;
  });

  private readonly TOP_TICKET_COUNT = 3;
  private readonly statusColorService = inject(StatusColorService);
  protected readonly sparePartsOverviewService = inject(
    SparePartsOverviewService,
  );

  private readonly ticketsSortChange$ = new BehaviorSubject<SpSortEventModel>({
    property: undefined,
    isDescending: false,
  });

  private readonly partsSortChange$ = new BehaviorSubject<SpSortEventModel>({
    property: undefined,
    isDescending: false,
  });

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    private applicationInsightsService: ApplicationInsightsService,
  ) {}

  private readonly allServiceTickets$ = this.sparePartsOverviewService.getServiceTickets$;
  private readonly allServiceParts$ = this.sparePartsOverviewService.getServiceParts$;
  public readonly mostConsumedPart$ =
    this.sparePartsOverviewService.mostConsumedPart$;


  public mostConsumedPartData$ = this.mostConsumedPart$.pipe(
    map((res) => ({
      description: `ID ${res.partNumber} - ${res.partDescription}`,
      totalConsumptionCount: res.totalConsumptionCount,
    })),
  );

  protected readonly topServiceParts$ = combineLatest({
    parts: this.allServiceParts$,
    sortChange: this.partsSortChange$,
  }).pipe(
    // pick top parts
    map(({ parts, sortChange }) => {
      const top = (parts ?? []).slice(0, this.TOP_TICKET_COUNT);
      return { parts: top, sortChange };
    }),
    map(({ parts, sortChange }) => {
      if (sortChange.property) {
        parts.sort((a, b) => {
          const aValue = a[sortChange.property as keyof SpPartsModel];
          const bValue = b[sortChange.property as keyof SpPartsModel];
          return (
            String(aValue)
              .toLocaleLowerCase()
              .localeCompare(String(bValue).toLocaleLowerCase()) *
            (sortChange.isDescending ? -1 : 1)
          );
        });
      }

      return parts;
    }),
  );

  protected readonly topTickets$ = combineLatest({
    tickets: this.allServiceTickets$,
    sortChange: this.ticketsSortChange$,
  }).pipe(
    // pick top tickets
    map(({ tickets, sortChange }) => {
      const topTickets = (tickets ?? [])
        .slice(0, this.TOP_TICKET_COUNT)
        .map((ticket) => ({
          ...ticket,
          statusColor: this.statusColorService.statusColor(
            ticket.operationalState as SpOperationalState,
          ),
        }));

      return { tickets: topTickets, sortChange };
    }),
    map(({ tickets, sortChange }) => {
      if (sortChange.property) {
        tickets.sort((a, b) => {
          const aValue =
            a[sortChange.property as keyof Omit<SpTicketModel, 'parts'>];
          const bValue =
            b[sortChange.property as keyof Omit<SpTicketModel, 'parts'>];
          return (
            String(aValue)
              .toLocaleLowerCase()
              .localeCompare(String(bValue).toLocaleLowerCase()) *
            (sortChange.isDescending ? -1 : 1)
          );
        });
      }
      return tickets;
    }),
  );

  protected readonly ticketCountDisplay$ = this.allServiceTickets$.pipe(
    map((tickets) => {
      let result = '(0/0)';
      if (tickets && tickets.length > 0) {
        result =
          tickets.length > this.TOP_TICKET_COUNT
            ? `(3/${tickets.length})`
            : `(${tickets.length}/${tickets.length})`;
      }
      return result;
    }),
  );

  public ngOnChanges(): void {
    this.sparePartsOverviewService.getSparePartsOverview.request({
      MaterialNumber: this.systemMaterialNumber,
      SerialNumber: this.systemSerialNumber,
      StartDate: moment.utc().subtract(365, 'days').format(ISO_DATE_FORMAT),
      EndDate: moment.utc().format(ISO_DATE_FORMAT),
    });
  }

  public ngOnInit(): void {
    this.sparePartsOverviewService.getHighestConsumedPart.request({
      materialNumber: this.systemMaterialNumber,
      serialNumber: this.systemSerialNumber,
    });
  }

  openDetailedView(): void {
    this.router.navigate(['spareParts', this.selectedTab()], {
      relativeTo: this.route,
    });
    const eventName = this.selectedTab() === this.TAB_SERVICE_TICKETS ? 'ServiceTicketsTabOpened' : 'ServicePartsTabOpened';
    this.applicationInsightsService.trackComponentUsageEvent(eventName, {
      serviceType: this.selectedTab(),
    });
  }

  protected onTicketsSortChange(change: SpSortEventModel): void {
    this.ticketsSortChange$.next(change);
  }

  protected onPartsSortChange(change: SpSortEventModel): void {
    this.partsSortChange$.next(change);
  }
}
